import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Loader2, Plus, Trash2, Pencil, LogOut, X, MapPin } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PLOT = {
  title: "",
  location: "Shujalpur, Madhya Pradesh",
  area_sqft: 1200,
  status: "available",
  facing: "East",
  description: "",
  image_url: "",
  featured: false,
};

function PlotModal({ open, plot, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_PLOT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(plot ? { ...EMPTY_PLOT, ...plot } : EMPTY_PLOT);
  }, [plot, open]);

  if (!open) return null;

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        area_sqft: Number(form.area_sqft),
        featured: !!form.featured,
      };
      if (plot?.id) {
        await api.put(`/admin/plots/${plot.id}`, payload);
        toast.success("Plot updated");
      } else {
        await api.post("/admin/plots", payload);
        toast.success("Plot created");
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0b] border border-[#2a2a2d] rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#6f6f74] focus:border-[#c5a059] focus:outline-none";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="plot-modal">
      <div className="bg-[#0f0f11] border border-[#2a2a2d] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#1f1f22]">
          <h2 className="font-serif text-2xl">{plot?.id ? "Edit Plot" : "Add Plot"}</h2>
          <button onClick={onClose} data-testid="plot-modal-close" className="text-[#8f8f94] hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <input data-testid="plot-input-title" placeholder="Title" className={inputCls} value={form.title} onChange={(e) => upd("title", e.target.value)} required />
          <input placeholder="Location" className={inputCls} value={form.location} onChange={(e) => upd("location", e.target.value)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="plot-input-area" type="number" placeholder="Area (sqft)" className={inputCls} value={form.area_sqft} onChange={(e) => upd("area_sqft", e.target.value)} required />
            <input placeholder="Facing (East / North / etc.)" className={inputCls} value={form.facing || ""} onChange={(e) => upd("facing", e.target.value)} />
          </div>
          <select data-testid="plot-input-status" className={inputCls} value={form.status} onChange={(e) => upd("status", e.target.value)}>
            <option value="available">available</option>
            <option value="reserved">reserved</option>
            <option value="sold">sold</option>
          </select>
          <input data-testid="plot-input-image" placeholder="Image URL" className={inputCls} value={form.image_url || ""} onChange={(e) => upd("image_url", e.target.value)} />
          <textarea placeholder="Description" className={inputCls + " min-h-[90px] resize-none"} value={form.description || ""} onChange={(e) => upd("description", e.target.value)} />
          <label className="flex items-center gap-2 text-sm text-[#cfcfd2]">
            <input data-testid="plot-input-featured" type="checkbox" checked={!!form.featured} onChange={(e) => upd("featured", e.target.checked)} className="accent-[#c5a059]" />
            Mark as featured
          </label>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-[#cfcfd2] border border-[#2a2a2d] rounded-md hover:bg-[#1a1a1c]">Cancel</button>
            <button data-testid="plot-modal-save" type="submit" disabled={saving} className="btn-gold px-6 py-2.5 rounded-md text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {plot?.id ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("plots");
  const [plots, setPlots] = useState([]);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPlots = () => {
    setBusy(true);
    api.get("/plots").then((r) => setPlots(r.data)).finally(() => setBusy(false));
  };
  const loadLeads = () => {
    setBusy(true);
    api.get("/admin/leads").then((r) => setLeads(r.data)).finally(() => setBusy(false));
  };

  useEffect(() => {
    if (!user) return;
    loadPlots();
    loadLeads();
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b]"><Loader2 className="animate-spin text-[#c5a059]" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const delPlot = async (id) => {
    if (!confirm("Delete this plot?")) return;
    await api.delete(`/admin/plots/${id}`);
    toast.success("Plot deleted");
    loadPlots();
  };
  const updateLeadStatus = async (id, status) => {
    await api.put(`/admin/leads/${id}`, { status });
    toast.success("Lead updated");
    loadLeads();
  };
  const delLead = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Lead deleted");
    loadLeads();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-[#1f1f22] bg-[#08080a] sticky top-0 z-40 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo_mark.png" alt="Aryavarta" className="h-10 w-10 object-contain" />
            <div>
              <p className="font-serif text-base leading-tight">Aryavarta Console</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#8f8f94]">Admin · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site" className="text-xs text-[#cfcfd2] hover:text-[#c5a059] px-4 py-2 border border-[#2a2a2d] rounded-full">View Site</Link>
            <button onClick={logout} data-testid="admin-logout" className="text-xs text-[#cfcfd2] hover:text-[#c5a059] px-4 py-2 border border-[#2a2a2d] rounded-full inline-flex items-center gap-2">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-px bg-[#1f1f22] border border-[#1f1f22] rounded-xl overflow-hidden mb-10">
          {[
            ["Total Plots", plots.length, "#c5a059"],
            ["Available", plots.filter((p) => p.status === "available").length, "#7ed992"],
            ["Reserved/Sold", plots.filter((p) => p.status !== "available").length, "#c5a059"],
            ["New Leads", leads.filter((l) => l.status === "new").length, "#7ed992"],
          ].map(([t, v, c]) => (
            <div key={t} className="bg-[#0a0a0b] p-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#8f8f94]">{t}</p>
              <p className="font-serif text-4xl mt-2" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-[#1f1f22]">
          {["plots", "leads"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-5 py-3 text-sm capitalize tracking-wide border-b-2 -mb-px transition ${
                tab === t ? "border-[#c5a059] text-white" : "border-transparent text-[#8f8f94] hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "plots" && (
          <section data-testid="admin-plots-section">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-[#cfcfd2]">{plots.length} listings</p>
              <button
                data-testid="admin-add-plot-btn"
                onClick={() => { setEditing(null); setModalOpen(true); }}
                className="btn-gold px-5 py-2.5 rounded-md text-sm font-medium inline-flex items-center gap-2"
              >
                <Plus size={14} /> Add Plot
              </button>
            </div>

            <div className="border border-[#1f1f22] rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-plots-table">
                <thead className="bg-[#0f0f11] text-[10px] tracking-[0.2em] uppercase text-[#8f8f94]">
                  <tr>
                    <th className="text-left p-4">Title</th>
                    <th className="text-left p-4">Area</th>
                    <th className="text-left p-4">Facing</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Featured</th>
                    <th className="p-4" />
                  </tr>
                </thead>
                <tbody>
                  {plots.map((p) => (
                    <tr key={p.id} className="border-t border-[#1f1f22] hover:bg-[#0f0f11]/60">
                      <td className="p-4 text-white">{p.title}</td>
                      <td className="p-4 text-[#cfcfd2]">{p.area_sqft} sqft</td>
                      <td className="p-4 text-[#cfcfd2]">{p.facing || "—"}</td>
                      <td className="p-4 text-[#cfcfd2] capitalize">{p.status}</td>
                      <td className="p-4 text-[#cfcfd2]">{p.featured ? "Yes" : "—"}</td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button data-testid={`plot-edit-${p.id}`} onClick={() => { setEditing(p); setModalOpen(true); }} className="p-2 text-[#cfcfd2] hover:text-[#c5a059] border border-[#2a2a2d] rounded-md">
                            <Pencil size={14} />
                          </button>
                          <button data-testid={`plot-delete-${p.id}`} onClick={() => delPlot(p.id)} className="p-2 text-[#cfcfd2] hover:text-red-400 border border-[#2a2a2d] rounded-md">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {plots.length === 0 && !busy && (
                    <tr><td colSpan={6} className="p-10 text-center text-[#8f8f94]">No plots yet. Add your first plot.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "leads" && (
          <section data-testid="admin-leads-section">
            <p className="text-sm text-[#cfcfd2] mb-5">{leads.length} enquiries</p>
            <div className="border border-[#1f1f22] rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-[#0f0f11] text-[10px] tracking-[0.2em] uppercase text-[#8f8f94]">
                  <tr>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Phone</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Interest</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Date</th>
                    <th className="p-4" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-[#1f1f22] hover:bg-[#0f0f11]/60">
                      <td className="p-4 text-white">{l.name}</td>
                      <td className="p-4 text-[#cfcfd2]">{l.phone}</td>
                      <td className="p-4 text-[#cfcfd2]">{l.email || "—"}</td>
                      <td className="p-4 text-[#cfcfd2]">{l.plot_interest || "—"}</td>
                      <td className="p-4">
                        <select
                          data-testid={`lead-status-${l.id}`}
                          value={l.status}
                          onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                          className="bg-[#0a0a0b] border border-[#2a2a2d] rounded-md px-2 py-1.5 text-xs text-white capitalize"
                        >
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="closed">closed</option>
                        </select>
                      </td>
                      <td className="p-4 text-[#8f8f94] text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <button data-testid={`lead-delete-${l.id}`} onClick={() => delLead(l.id)} className="p-2 text-[#cfcfd2] hover:text-red-400 border border-[#2a2a2d] rounded-md">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && !busy && (
                    <tr><td colSpan={7} className="p-10 text-center text-[#8f8f94]">No enquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      <PlotModal open={modalOpen} plot={editing} onClose={() => setModalOpen(false)} onSaved={loadPlots} />
    </div>
  );
}
